import React, { useState } from 'react';

// A company's or a store's stand-in logo: a disc in a tinted fill with the
// name's initials in the same hue. The Manager app draws the same thing
// (managerapp lib/widgets/monogram.dart): the same initials rule, the same
// hash, the same six pairs in the same order, so a restaurant has one colour
// on every surface.
export const MONOGRAM_PALETTE = [
  { fill: '#EFE9FF', ink: '#6B3FD6' }, // Purple
  { fill: '#E3EEFF', ink: '#1F5FD1' }, // Blue
  { fill: '#FFEBDB', ink: '#C25A00' }, // Orange
  { fill: '#DDF4F0', ink: '#0F7C70' }, // Teal
  { fill: '#FFE5EC', ink: '#C2305A' }, // Rose
  { fill: '#FFF2CC', ink: '#8A5A00' }, // Amber
];

const SEPARATORS = new Set([' ', '-', '–', '·', '&']);
const STARTS_WITH_LETTER_OR_DIGIT = /^[\p{L}\p{N}]/u;

// The first letter of the first two words, upper case. Words are split on
// spaces, hyphens, middle dots and ampersands; a word that does not start
// with a letter or a digit (an emoji, a bracket) is skipped. No word, no
// initials.
export const monogramInitials = (name) => {
  const tokens = [];
  let current = '';
  [...(name || '')].forEach((ch) => {
    if (SEPARATORS.has(ch)) {
      if (current) tokens.push(current);
      current = '';
    } else {
      current += ch;
    }
  });
  if (current) tokens.push(current);
  return tokens
    .filter((t) => STARTS_WITH_LETTER_OR_DIGIT.test(t))
    // One character per word: upper-casing can expand a letter ('ß' to 'SS').
    .map((t) => [...[...t][0].toUpperCase()][0])
    .slice(0, 2)
    .join('');
};

// Which palette pair a name gets: hash = hash * 31 + UTF-16 code unit in
// 32-bit unsigned arithmetic, mod 6. h < 2^32, so h * 31 + code stays an
// exact double and `>>> 0` takes it mod 2^32, as the app's `& 0xFFFFFFFF`.
export const monogramIndex = (name) => {
  const text = name || '';
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (h * 31 + text.charCodeAt(i)) >>> 0;
  return h % MONOGRAM_PALETTE.length;
};

// Decorative: the name is always beside it. A name with no initials is the
// fill alone, never a "?". The 1px inset ring in the pair's ink at 20 %
// keeps the disc readable on a white, a hovered and a blue-tinted row alike.
export const Monogram = ({ name, size = 40, className = '' }) => {
  const { fill, ink } = MONOGRAM_PALETTE[monogramIndex(name)];
  return (
    <span
      aria-hidden="true"
      data-testid="monogram"
      className={`inline-flex flex-none select-none items-center justify-center rounded-full leading-none ${className}`.trim()}
      style={{
        width: size,
        height: size,
        backgroundColor: fill,
        color: ink,
        fontSize: Math.round(size * 0.4),
        fontWeight: 900,
        letterSpacing: '-0.025em',
        boxShadow: `inset 0 0 0 1px ${ink}33`,
      }}
    >
      {monogramInitials(name)}
    </span>
  );
};

// A logo wins whenever it loads; no logo, or one that fails, is the
// monogram in the same box, so nothing shifts. A new src is tried again.
// `alt` is the name a real logo carries for assistive tech (empty when the
// name is written beside it); the monogram is always aria-hidden.
export const LogoOrMonogram = ({ src, name, alt = '', size = 40, className = '', imgClassName = '' }) => {
  const [failedSrc, setFailedSrc] = useState(null);
  if (!src || failedSrc === src) {
    return <Monogram name={name} size={size} className={className} />;
  }
  return (
    <img
      data-testid="logo-image"
      className={imgClassName}
      style={{ width: size, height: size }}
      src={src}
      alt={alt}
      onError={() => setFailedSrc(src)}
    />
  );
};
